import format from 'string-template';

import texts from '../texts';

const getAcDisplayName = ({ userName, isLiaison }) => (
  isLiaison ? format(texts.liaisonAcName, { acName: userName }) : userName
);

const getAcNamesByCoach = (acAssignments) => acAssignments
  .reduce((acc, assignment) => {
    acc[assignment.coach] = [...(acc[assignment.coach] || []), getAcDisplayName(assignment)];
    return acc;
  }, {});

/**
 * Whether a coach has Assistant Coaching turned on. Only those coaches can be in the pool: the pool is
 * served by the AC organization, and turning AC off takes a coach out of it.
 * @param {Object} coach - The coach (a `Coach` model or a plain object with `isACEnabled`).
 * @return {boolean} True only when AC is explicitly enabled.
 */
const isPoolEligible = ({ isACEnabled }) => isACEnabled === true;

/**
 * Whether the coach with `coachId` can be in the pool, looked up in `coaches` since callers often hold only
 * the coach's id and name. A coach missing from the list is not eligible.
 * @param {Array<Object>} coaches - The coaches to look in.
 * @param {string} coachId - The coach's id.
 * @return {boolean} True when the coach is on the list with AC enabled.
 */
const isCoachPoolEligible = (coaches, coachId) => coaches
  .some((coach) => coach.id === coachId && isPoolEligible(coach));

const getPoolCandidates = (coaches, pooledCoachIds) => {
  const excludedCoachIds = new Set(pooledCoachIds);

  return coaches
    .filter((coach) => !!coach.name && !excludedCoachIds.has(coach.id) && isPoolEligible(coach))
    .map(({ id, name }) => ({ id, name }))
    .sort((a, b) => a.name.localeCompare(b.name));
};

const getPoolRosterRows = (poolAssignments, coachNamesById) => poolAssignments
  .map(({
    id,
    coach,
    createdAt,
    createdByUserName,
  }) => ({
    id,
    coachId: coach,
    coachName: coachNamesById[coach] || texts.unknownCoach,
    createdAt,
    createdByUserName,
  }))
  .sort((a, b) => a.coachName.localeCompare(b.coachName));

const getPoolTransitions = (coaches, acNamesByCoach) => coaches.map(({ id, name }) => ({
  id,
  name,
  currentAcNames: acNamesByCoach[id] || [],
}));

export {
  getAcDisplayName,
  getAcNamesByCoach,
  getPoolCandidates,
  getPoolRosterRows,
  getPoolTransitions,
  isCoachPoolEligible,
};
